import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, getFormValues } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea/TextArea';

interface SaveInquiryTemplateVersionModalProps {
  hasLocalDraft: boolean;
  inquiryTemplateVersion: InquiryTemplateVersion;
  hideSaveModal: () => void;
  handleSave: () => void;
}

const getHeaderText = (hasLocalDraft: boolean, inquiryTemplateVersion: InquiryTemplateVersion) => {
  if (hasLocalDraft) {
    if (inquiryTemplateVersion.id == null) {
      // Saving will create a new draft
      return 'Create new draft from restored?';
    } else {
      // Saving will update existing draft
      return 'Update existing draft with restored?';
    }
  } else {
    if (inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published) {
      // Saving will create a new draft
      return 'Create new draft?';
    } else {
      // Saving will update existing draft
      return 'Update existing draft?';
    }
  }
};

export const SaveInquiryTemplateVersionModal = ({
  hasLocalDraft,
  inquiryTemplateVersion,
  hideSaveModal,
  handleSave,
}: SaveInquiryTemplateVersionModalProps) => {
  const editorFormDescription = useSelector<RootState, any>(
    (state) => getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state) ?? {},
  ).description;
  const [saveModalDescription, setSaveModalDescription] = useState(
    inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published
      ? ''
      : editorFormDescription,
  );

  const dispatch = useDispatch();

  return (
    <ModalContainer>
      <ModalHeader onClose={hideSaveModal}>
        {getHeaderText(hasLocalDraft, inquiryTemplateVersion)}
      </ModalHeader>

      <ModalBody>
        <Paragraph>Your changes won&apos;t be live until you publish the template</Paragraph>
        <Spacer height={3} />
        <TextArea
          id='description'
          name='description'
          placeholder='Describe the changes in this version (optional)'
          value={saveModalDescription}
          onChange={(e) => setSaveModalDescription(e.target.value)}
          autoFocus
        />
        <Spacer height={2} />
        <HStack equal={true} spacing={Spacing[4]}>
          <Button
            autoFocus={true}
            id='save-inquiry-template-version-modal__cancel'
            variant='basic'
            onClick={hideSaveModal}
          >
            Cancel
          </Button>
          <Button
            id='save-inquiry-template-version-modal__confirm'
            variant='primary'
            onClick={async () => {
              await dispatch(
                change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'description', saveModalDescription),
              );
              hideSaveModal();
              handleSave();
            }}
            data-test='save-template-version-modal__save'
          >
            Save
          </Button>
        </HStack>
      </ModalBody>
    </ModalContainer>
  );
};
